/**
 * LLM-Powered Task Guidance Hook
 *
 * Provides state management and API integration for LLM-assisted
 * task organization and completion guidance.
 *
 * Features:
 * - Organize tasks by priority, urgency, and context
 * - Generate completion guidance from task metadata
 * - Provide step-by-step instructions
 * - Track task progress with AI assistance
 *
 * Part of Task-Centric Architecture (Plan Option D)
 */

import { useCallback, useMemo, useReducer, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

// ============================================================================
// GraphQL Operations
// ============================================================================

const GENERATE_TASK_GUIDANCE = gql`
  mutation GenerateTaskGuidance($input: GenerateTaskGuidanceInput!, $userId: String!) {
    generateTaskGuidance(input: $input, userId: $userId) {
      guidance {
        guidanceId
        taskId
        steps {
          stepNumber
          instruction
          estimatedDuration
          requiredInputs
          validationRules
        }
        tips
        warnings
        complianceNotes
        relatedDocumentation
        confidence
      }
      organizationSuggestions {
        groupBy
        sortOrder
        priorityOverrides {
          taskId
          suggestedPriority
          reason
        }
      }
    }
  }
`;

const ORGANIZE_TASKS = gql`
  mutation OrganizeTasks($input: OrganizeTasksInput!, $userId: String!) {
    organizeTasks(input: $input, userId: $userId) {
      organizedTasks {
        taskId
        groupKey
        groupLabel
        sortOrder
        urgencyScore
        priorityReason
        suggestedStartTime
      }
      groups {
        groupKey
        label
        taskCount
        description
        icon
      }
      summary {
        totalTasks
        criticalCount
        overdueCount
        suggestedFocus
        estimatedTotalDuration
      }
    }
  }
`;

const GET_TASK_COMPLETION_HINTS = gql`
  query GetTaskCompletionHints($tenantId: String!, $taskId: String!, $userId: String!) {
    taskCompletionHints(tenantId: $tenantId, taskId: $taskId, userId: $userId) {
      inputFieldGuidance {
        fieldName
        hint
        examples
        validationHelp
      }
      quickActions {
        actionId
        label
        description
        icon
        prefillData
      }
      contextualInfo {
        key
        value
        source
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface GuidanceStep {
  stepNumber: number;
  instruction: string;
  estimatedDuration?: string;
  requiredInputs?: string[];
  validationRules?: string[];
}

export interface TaskGuidance {
  guidanceId: string;
  taskId: string;
  steps: GuidanceStep[];
  tips: string[];
  warnings: string[];
  complianceNotes: string[];
  relatedDocumentation: string[];
  confidence: number;
}

export interface PriorityOverride {
  taskId: string;
  suggestedPriority: 'CRITICAL' | 'HIGH' | 'NORMAL' | 'LOW';
  reason: string;
}

export interface OrganizationSuggestion {
  groupBy: string;
  sortOrder: 'priority' | 'dueDate' | 'client' | 'type';
  priorityOverrides: PriorityOverride[];
}

export interface OrganizedTask {
  taskId: string;
  groupKey: string;
  groupLabel: string;
  sortOrder: number;
  urgencyScore: number;
  priorityReason: string;
  suggestedStartTime?: string;
}

export interface TaskGroup {
  groupKey: string;
  label: string;
  taskCount: number;
  description?: string;
  icon?: string;
}

export interface TaskSummary {
  totalTasks: number;
  criticalCount: number;
  overdueCount: number;
  suggestedFocus: string;
  estimatedTotalDuration?: string;
}

export interface InputFieldGuidance {
  fieldName: string;
  hint: string;
  examples: string[];
  validationHelp?: string;
}

export interface QuickAction {
  actionId: string;
  label: string;
  description: string;
  icon?: string;
  prefillData?: Record<string, unknown>;
}

export interface ContextualInfo {
  key: string;
  value: string;
  source: string;
}

export interface CompletionHints {
  inputFieldGuidance: InputFieldGuidance[];
  quickActions: QuickAction[];
  contextualInfo: ContextualInfo[];
}

// ============================================================================
// State Management
// ============================================================================

interface GuidanceState {
  currentTaskId: string | null;
  guidance: TaskGuidance | null;
  organizedTasks: OrganizedTask[];
  groups: TaskGroup[];
  summary: TaskSummary | null;
  completionHints: CompletionHints | null;
  organizationSuggestion: OrganizationSuggestion | null;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

type GuidanceAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_GUIDANCE'; payload: TaskGuidance }
  | { type: 'SET_ORGANIZED_TASKS'; payload: { tasks: OrganizedTask[]; groups: TaskGroup[]; summary: TaskSummary } }
  | { type: 'SET_COMPLETION_HINTS'; payload: CompletionHints }
  | { type: 'SET_ORGANIZATION_SUGGESTION'; payload: OrganizationSuggestion }
  | { type: 'SET_CURRENT_TASK'; payload: string | null }
  | { type: 'CLEAR_GUIDANCE' };

const initialState: GuidanceState = {
  currentTaskId: null,
  guidance: null,
  organizedTasks: [],
  groups: [],
  summary: null,
  completionHints: null,
  organizationSuggestion: null,
  loading: false,
  error: null,
  lastUpdated: null,
};

function guidanceReducer(state: GuidanceState, action: GuidanceAction): GuidanceState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload, error: null };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_GUIDANCE':
      return {
        ...state,
        guidance: action.payload,
        currentTaskId: action.payload.taskId,
        loading: false,
        lastUpdated: new Date(),
      };
    case 'SET_ORGANIZED_TASKS':
      return {
        ...state,
        organizedTasks: action.payload.tasks,
        groups: action.payload.groups,
        summary: action.payload.summary,
        loading: false,
        lastUpdated: new Date(),
      };
    case 'SET_COMPLETION_HINTS':
      return {
        ...state,
        completionHints: action.payload,
        loading: false,
      };
    case 'SET_ORGANIZATION_SUGGESTION':
      return {
        ...state,
        organizationSuggestion: action.payload,
      };
    case 'SET_CURRENT_TASK':
      return {
        ...state,
        currentTaskId: action.payload,
        guidance: action.payload === state.currentTaskId ? state.guidance : null,
        completionHints: action.payload === state.currentTaskId ? state.completionHints : null,
      };
    case 'CLEAR_GUIDANCE':
      return {
        ...state,
        guidance: null,
        completionHints: null,
        currentTaskId: null,
      };
    default:
      return state;
  }
}

// ============================================================================
// Hook Options & Return Types
// ============================================================================

export interface UseLlmTaskGuidanceOptions {
  userId: string;
  tenantId: string;
  userRole?: string;
  userPreferences?: {
    preferredGrouping?: 'client' | 'type' | 'priority' | 'time';
    workingHours?: { start: string; end: string };
    maxConcurrentTasks?: number;
  };
}

export interface UseLlmTaskGuidanceResult {
  // State
  state: GuidanceState;

  // Operations
  generateGuidance: (taskId: string, taskContext?: Record<string, unknown>) => Promise<TaskGuidance | null>;
  organizeTasks: (taskIds: string[], context?: { clientIds?: string[]; shift?: string }) => Promise<void>;
  getCompletionHints: (taskId: string) => Promise<CompletionHints | null>;
  setCurrentTask: (taskId: string | null) => void;
  clearGuidance: () => void;

  // Computed values
  isLoading: boolean;
  hasGuidance: boolean;
  hasOrganizedTasks: boolean;
  currentGuidance: TaskGuidance | null;
  taskGroups: TaskGroup[];
  suggestedFocus: string | null;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useLlmTaskGuidance(options: UseLlmTaskGuidanceOptions): UseLlmTaskGuidanceResult {
  const { userId, tenantId, userRole, userPreferences } = options;
  const [state, dispatch] = useReducer(guidanceReducer, initialState);

  // Track request ID for race condition prevention
  const requestIdRef = useRef(0);

  // GraphQL mutations
  const [generateGuidanceMutation] = useMutation<Record<string, any>>(GENERATE_TASK_GUIDANCE);
  const [organizeTasksMutation] = useMutation<Record<string, any>>(ORGANIZE_TASKS);

  // Lazy query for completion hints
  const { refetch: fetchHints } = useQuery<Record<string, any>>(GET_TASK_COMPLETION_HINTS, {
    skip: true,
    variables: { tenantId, taskId: '', userId },
  });

  // Generate guidance for a specific task
  const generateGuidance = useCallback(
    async (taskId: string, taskContext?: Record<string, unknown>): Promise<TaskGuidance | null> => {
      const currentRequestId = ++requestIdRef.current;
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_CURRENT_TASK', payload: taskId });

      try {
        const { data } = await generateGuidanceMutation({
          variables: {
            input: {
              tenantId,
              taskId,
              userRole,
              taskContext: taskContext ?? {},
              preferences: userPreferences,
            },
            userId,
          },
        });

        // Check for race condition
        if (currentRequestId !== requestIdRef.current) {
          return null;
        }

        if (data?.generateTaskGuidance) {
          const { guidance, organizationSuggestions } = data.generateTaskGuidance;
          dispatch({ type: 'SET_GUIDANCE', payload: guidance });
          if (organizationSuggestions) {
            dispatch({ type: 'SET_ORGANIZATION_SUGGESTION', payload: organizationSuggestions });
          }
          return guidance;
        }
        return null;
      } catch (err) {
        if (currentRequestId === requestIdRef.current) {
          dispatch({
            type: 'SET_ERROR',
            payload: err instanceof Error ? err.message : 'Failed to generate guidance',
          });
        }
        return null;
      }
    },
    [generateGuidanceMutation, tenantId, userId, userRole, userPreferences]
  );

  // Organize multiple tasks
  const organizeTasks = useCallback(
    async (taskIds: string[], context?: { clientIds?: string[]; shift?: string }): Promise<void> => {
      const currentRequestId = ++requestIdRef.current;
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const { data } = await organizeTasksMutation({
          variables: {
            input: {
              tenantId,
              taskIds,
              userRole,
              context: context ?? {},
              preferences: userPreferences,
            },
            userId,
          },
        });

        // Check for race condition
        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        if (data?.organizeTasks) {
          const { organizedTasks, groups, summary } = data.organizeTasks;
          dispatch({
            type: 'SET_ORGANIZED_TASKS',
            payload: { tasks: organizedTasks, groups, summary },
          });
        }
      } catch (err) {
        if (currentRequestId === requestIdRef.current) {
          dispatch({
            type: 'SET_ERROR',
            payload: err instanceof Error ? err.message : 'Failed to organize tasks',
          });
        }
      }
    },
    [organizeTasksMutation, tenantId, userId, userRole, userPreferences]
  );

  // Get completion hints for a task
  const getCompletionHints = useCallback(
    async (taskId: string): Promise<CompletionHints | null> => {
      try {
        const { data } = await fetchHints({ tenantId, taskId, userId });
        if (data?.taskCompletionHints) {
          dispatch({ type: 'SET_COMPLETION_HINTS', payload: data?.taskCompletionHints });
          return data?.taskCompletionHints ?? null;
        }
        return null;
      } catch (err) {
        dispatch({
          type: 'SET_ERROR',
          payload: err instanceof Error ? err.message : 'Failed to get completion hints',
        });
        return null;
      }
    },
    [fetchHints, tenantId, userId]
  );

  // Set current task
  const setCurrentTask = useCallback((taskId: string | null) => {
    dispatch({ type: 'SET_CURRENT_TASK', payload: taskId });
  }, []);

  // Clear guidance
  const clearGuidance = useCallback(() => {
    dispatch({ type: 'CLEAR_GUIDANCE' });
  }, []);

  // Computed values
  const computed = useMemo(
    () => ({
      isLoading: state.loading,
      hasGuidance: state.guidance !== null,
      hasOrganizedTasks: state.organizedTasks.length > 0,
      currentGuidance: state.guidance,
      taskGroups: state.groups,
      suggestedFocus: state.summary?.suggestedFocus ?? null,
    }),
    [state]
  );

  return {
    state,
    generateGuidance,
    organizeTasks,
    getCompletionHints,
    setCurrentTask,
    clearGuidance,
    ...computed,
  };
}
